import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import type { AppCore } from '@core/bootstrap'
import type { Category, Product } from '@core/db/models'
import { CategoryImage } from '@core/components/CategoryImage'
import {
  CATEGORY_ICON_KINDS,
  CategoryIconTile,
  pickCategoryIcon,
  type CategoryIconKind
} from '@core/components/CategoryIcon'
import { CATEGORY_PALETTE, categoryVisual } from '@core/utils/categoryVisual'
import { blobToBase64, validateImageFile } from '@core/utils/imageAsset'
import { syncService } from '@core/services/sync/SyncService'
import {
  btn,
  btnBase,
  btnSize,
  fieldLabel,
  hint,
  input,
  modalSm,
  overlay,
  table,
  tableWrap,
  td,
  tdNum,
  th,
  thNum
} from '@ui/classes'
import { errBanner, okBanner } from '@ui/admin'
import { useAuthToken } from './useAuthToken'
import { CLOUD_API } from './shared'

/** Черновик формы категории (строки — из inputs, не из модели). */
interface CategoryDraft {
  name: string
  parentName: string
  department: string
  isActive: boolean
}

const EMPTY_DRAFT: CategoryDraft = {
  name: '',
  parentName: '',
  department: 'Магазин',
  isActive: true
}

function draftFrom(category: Category): CategoryDraft {
  return {
    name: category.name,
    parentName: category.parentName ?? '',
    department: category.department ?? '',
    isActive: category.isActive
  }
}

/** Понятный текст ошибки облачного API. */
function apiError(payload: { error?: string }, status: number): string {
  switch (payload.error) {
    case 'category_exists':
      return 'Категория с таким названием уже есть'
    case 'category_not_empty':
      return 'В категории есть товары или подкатегории — сначала перенесите их'
    case 'category_not_found':
      return 'Категория не найдена — обновите список'
    case 'parent_not_found':
      return 'Родительская категория не найдена'
    case 'category_cycle':
      return 'Нельзя вложить категорию в саму себя или в своего потомка'
    case 'name is required':
      return 'Укажите название категории'
    default:
      return `Не удалось сохранить: HTTP ${status}`
  }
}

/** Сериализовать SVG-иконку в PNG 256×256 (для кассы и облака). */
async function svgToPng(svg: SVGSVGElement, size = 256): Promise<Blob> {
  let source = new XMLSerializer().serializeToString(svg)
  // Safari/iOS не грузит SVG data URL без явного namespace.
  if (!source.includes('xmlns=')) {
    source = source.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"')
  }
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(source)}`
  const image = new Image()
  await new Promise<void>((resolve, reject) => {
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('Не удалось отрисовать иконку'))
    image.src = url
  })
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas недоступен')
  ctx.drawImage(image, 0, 0, size, size)
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Не удалось создать PNG'))),
      'image/png'
    )
  })
}

/**
 * Управление деревом категорий.
 *
 * UX: одна таблица-дерево, ручка перетаскивания для порядка и одна форма в
 * модалке. Параметры карточки FusionPOS, которые действительно влияют на
 * кассу: название, родитель («Главный экран»), отдел, активность, иконка.
 * Налоги/НДС/скидка — свойства товара и чека, а не категории.
 */
export function CategoriesSection({
  core,
  refreshKey
}: {
  core: AppCore
  refreshKey: number
}) {
  const { catalog, assets } = core
  const token = useAuthToken()
  const [categories, setCategories] = useState<Category[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [search, setSearch] = useState('')
  const [editingName, setEditingName] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CategoryDraft>(EMPTY_DRAFT)
  const [iconPicker, setIconPicker] = useState<{
    name: string
    kind: CategoryIconKind
    colorKey: number
  } | null>(null)
  const [dragState, setDragState] = useState<{
    name: string
    parent: string | null
  } | null>(null)
  const [dropTarget, setDropTarget] = useState<string | null>(null)
  const [dragGhost, setDragGhost] = useState<{
    name: string
    x: number
    y: number
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTarget = useRef<string | null>(null)
  const previewRef = useRef<SVGSVGElement>(null)

  const refresh = useCallback(async () => {
    const [tree, list] = await Promise.all([
      catalog.listCategoryTree(),
      catalog.listActiveProducts()
    ])
    setCategories(tree)
    setProducts(list)
  }, [catalog])

  useEffect(() => {
    void refresh().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : String(e))
    )
  }, [refresh, refreshKey])

  const api = useCallback(
    async (path: string, init: RequestInit): Promise<unknown> => {
      if (!token) {
        throw new Error('Нужен вход в облако: изменения категорий сохраняются в облаке')
      }
      const response = await fetch(`${CLOUD_API}/catalog${path}`, {
        ...init,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      })
      const payload = (await response.json().catch(() => ({}))) as {
        error?: string
      }
      if (!response.ok) {
        throw new Error(apiError(payload, response.status))
      }
      return payload
    },
    [token]
  )

  /** После изменения — подтянуть облако в Dexie, чтобы UI не ждал таймера. */
  const syncAfterChange = useCallback(async () => {
    await syncService.syncAll().catch(() => {
      // Офлайн/нет токена: локальное дерево обновится следующим синком.
    })
    await refresh()
  }, [refresh])

  const openCreate = () => {
    setError(null)
    setMessage(null)
    setEditingName(null)
    setDraft(EMPTY_DRAFT)
    setCreating(true)
  }

  const openEdit = (category: Category) => {
    setError(null)
    setMessage(null)
    setCreating(false)
    setEditingName(category.name)
    setDraft(draftFrom(category))
  }

  const closeForm = () => {
    setCreating(false)
    setEditingName(null)
    setDraft(EMPTY_DRAFT)
  }

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault()
    const name = draft.name.trim()
    if (!name) {
      setError('Укажите название категории')
      return
    }
    setBusy(true)
    setError(null)
    setMessage(null)
    const body = {
      name,
      parent_name: draft.parentName.trim() || null,
      department: draft.department.trim() || null,
      is_active: draft.isActive
    }
    try {
      if (creating) {
        await api('/categories', { method: 'POST', body: JSON.stringify(body) })
        setMessage(`Категория «${name}» создана`)
      } else if (editingName) {
        await api(`/categories/${encodeURIComponent(editingName)}`, {
          method: 'PATCH',
          body: JSON.stringify(body)
        })
        setMessage(`Категория «${name}» сохранена`)
      }
      closeForm()
      await syncAfterChange()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const removeCategory = async (category: Category) => {
    if (!window.confirm(`Удалить категорию «${category.name}»?`)) return
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await api(`/categories/${encodeURIComponent(category.name)}`, {
        method: 'DELETE'
      })
      // Локально удаляем сразу: синхронизация не убирает отсутствующие строки.
      await core.catalog.removeCategoryLocal(category.name)
      setMessage(`Категория «${category.name}» удалена`)
      await syncAfterChange()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const toggleActive = async (category: Category, active: boolean) => {
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      await catalog.setCategoryActive(category.name, active)
      if (token) {
        await api(`/categories/${encodeURIComponent(category.name)}`, {
          method: 'PATCH',
          body: JSON.stringify({ is_active: active })
        })
      }
      setMessage(
        active
          ? `Категория «${category.name}» включена`
          : `Категория «${category.name}» отключена: скрыта с кассы`
      )
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const pickImage = (name: string) => {
    uploadTarget.current = name
    fileInputRef.current?.click()
  }

  const onFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    const name = uploadTarget.current
    e.target.value = ''
    if (!file || !name) return
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const result = await validateImageFile(file)
      if (!result.ok) {
        setError(result.error)
        return
      }
      await assets.putCategoryImage(name, result.image.blob, result.image.mime)
      if (token) {
        const data_base64 = await blobToBase64(result.image.blob)
        const response = await fetch(
          `${CLOUD_API}/catalog/categories/${encodeURIComponent(name)}/image`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ mime: result.image.mime, data_base64 })
          }
        )
        if (!response.ok) {
          throw new Error(`Не удалось сохранить в облаке: HTTP ${response.status}`)
        }
      }
      await catalog.setCategoryHasImage(name, true)
      setMessage(
        token
          ? `Картинка «${name}» сохранена`
          : `Картинка «${name}» сохранена только на этом устройстве`
      )
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const openIconPicker = () => {
    const name = draft.name.trim()
    if (!name) {
      setError('Сначала укажите название категории')
      return
    }
    setError(null)
    setIconPicker({
      name,
      kind: pickCategoryIcon(name),
      colorKey: categoryVisual(name).colorKey
    })
  }

  const applyGeneratedIcon = async () => {
    if (!iconPicker) return
    const name = iconPicker.name.trim()
    const svg = previewRef.current
    if (!name) {
      setError('Укажите название категории')
      return
    }
    if (!svg) {
      setError('Не удалось подготовить иконку')
      return
    }
    setBusy(true)
    setError(null)
    setMessage(null)
    try {
      const blob = await svgToPng(svg, 256)
      await assets.putCategoryImage(name, blob, 'image/png')
      if (token) {
        const data_base64 = await blobToBase64(blob)
        const response = await fetch(
          `${CLOUD_API}/catalog/categories/${encodeURIComponent(name)}/image`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ mime: 'image/png', data_base64 })
          }
        )
        if (!response.ok) {
          throw new Error(`Не удалось сохранить в облаке: HTTP ${response.status}`)
        }
      }
      await catalog.setCategoryHasImage(name, true)
      setMessage(`Иконка «${name}» применена`)
      setIconPicker(null)
      await refresh()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  /** Перестановка внутри одного уровня (главный экран или подкатегории). */
  const reorder = useCallback(
    async (
      source: { name: string; parent: string | null },
      targetName: string
    ) => {
      const siblings = (parent: string | null): Category[] =>
        parent == null
          ? categories.filter((c) => c.parentName == null)
          : categories.filter((c) => c.parentName === parent)
      const list = siblings(source.parent)
      const from = list.findIndex((c) => c.name === source.name)
      const to = list.findIndex((c) => c.name === targetName)
      if (from < 0 || to < 0 || from === to) return

      const next = [...list]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      const updates = next.map((c, index) => ({
        name: c.name,
        sort_order: index
      }))

      // Оптимистично: список переставляется сразу, без ожидания сети.
      setCategories((prev) =>
        prev.map((c) => {
          const update = updates.find((u) => u.name === c.name)
          return update ? { ...c, sortOrder: update.sort_order } : c
        })
      )

      // Локальный порядок — чтобы строка встала на место и после refresh.
      await catalog
        .setCategoryOrderLocal(
          updates.map((u) => ({ name: u.name, sortOrder: u.sort_order }))
        )
        .catch(() => undefined)
      await refresh()

      if (!token) return
      try {
        await api('/categories/reorder', {
          method: 'POST',
          body: JSON.stringify({ items: updates })
        })
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err))
        await refresh()
      }
    },
    [categories, token, api, refresh, catalog]
  )

  // Перетаскивание: pointer-события работают и мышью, и пальцем.
  useEffect(() => {
    if (!dragState) return
    const targetAt = (x: number, y: number): string | null => {
      const el = document
        .elementFromPoint(x, y)
        ?.closest('[data-category-row]')
      return el?.getAttribute('data-category-row') ?? null
    }
    const onMove = (e: PointerEvent) => {
      setDropTarget(targetAt(e.clientX, e.clientY))
      setDragGhost((current) =>
        current ? { ...current, x: e.clientX, y: e.clientY } : current
      )
    }
    const onUp = (e: PointerEvent) => {
      const target = targetAt(e.clientX, e.clientY)
      const source = dragState
      setDragState(null)
      setDropTarget(null)
      setDragGhost(null)
      if (target && target !== source.name) void reorder(source, target)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [dragState, reorder])

  // Счётчики: по верхнему уровню и по паре «верхний уровень + лист».
  const counts = new Map<string, number>()
  for (const p of products) {
    const parent = p.categoryParent?.trim() || p.category?.trim() || 'Общее'
    const leaf = p.category?.trim() || 'Общее'
    counts.set(parent, (counts.get(parent) ?? 0) + 1)
    if (leaf !== parent) {
      const key = `${parent}\u0000${leaf}`
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }

  // Порядок задаётся sortOrder (drag&drop); имя — стабильный tie-break.
  const byOrder = (a: Category, b: Category): number =>
    (a.sortOrder ?? Number.MAX_SAFE_INTEGER) -
      (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
    a.name.localeCompare(b.name, 'ru')

  const names = new Set(categories.map((c) => c.name))
  const top = categories.filter((c) => c.parentName == null).sort(byOrder)
  const orphans = categories
    .filter((c) => c.parentName != null && !names.has(c.parentName))
    .sort(byOrder)
  const childrenOf = (name: string): Category[] =>
    categories.filter((c) => c.parentName === name).sort(byOrder)

  const query = search.trim().toLowerCase()
  const matches = (category: Category): boolean =>
    !query || category.name.toLowerCase().includes(query)

  const parentOptions = categories.filter((c) => c.name !== editingName)
  const editingHasImage =
    editingName != null &&
    categories.find((c) => c.name === editingName)?.hasImage === true

  const renderIconCell = (category: Category, size: number) => (
    <div className="flex items-center gap-1.5">
      <button
        type="button"
        className="cursor-grab touch-none rounded px-1 text-muted hover:text-ink active:cursor-grabbing"
        aria-label={`Переместить «${category.name}»`}
        title="Перетащите вверх или вниз, чтобы изменить порядок"
        onPointerDown={(e) => {
          e.preventDefault()
          setDragState({ name: category.name, parent: category.parentName })
          setDragGhost({ name: category.name, x: e.clientX, y: e.clientY })
        }}
      >
        ⠿
      </button>
      <CategoryImage
        name={category.name}
        hasImage={category.hasImage}
        size={size}
      />
    </div>
  )

  const renderActions = (category: Category) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        className={cx(btnBase, btn.secondary, btnSize.sm)}
        onClick={() => openEdit(category)}
        disabled={busy}
      >
        Изменить
      </button>
      <button
        type="button"
        className={cx(btnBase, btn.softDanger, btnSize.sm)}
        onClick={() => void removeCategory(category)}
        disabled={busy}
        title="Удалить можно только пустую категорию"
      >
        Удалить
      </button>
    </div>
  )

  const rowClass = (name: string) =>
    cx(
      'transition-colors',
      dropTarget === name && 'bg-soft ring-2 ring-accent/40',
      dragState?.name === name && 'opacity-40'
    )

  const formOpen = creating || editingName != null

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-small text-muted">Всего: {categories.length}</span>
        <div className="flex flex-wrap items-center gap-2">
          <input
            className={cx(input, 'w-[220px]')}
            placeholder="Найти категорию…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className={cx(btnBase, btn.success, btnSize.md)}
            onClick={openCreate}
            disabled={busy}
          >
            + Новая категория
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".svg,.png,.jpg,.jpeg,.webp"
        className="hidden"
        onChange={onFileSelected}
      />

      {!token && (
        <p className={hint}>
          Без входа в облако дерево редактируется только на этом устройстве.
          Создание, переименование и удаление категорий сохраняются в облаке.
        </p>
      )}
      {message && <p className={okBanner}>{message}</p>}
      {error && <p className={errBanner}>{error}</p>}

      {categories.length === 0 ? (
        <p className={hint}>
          Дерево категорий пусто. Создайте первую категорию или импортируйте
          выгрузку категорий в блоке импорта.
        </p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>Иконка</th>
                <th className={th}>Название</th>
                <th className={th}>Родитель</th>
                <th className={th}>Отдел</th>
                <th className={th}>Активно</th>
                <th className={thNum}>Товаров</th>
                <th className={th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {top.filter(matches).map((category) => (
                <Fragment key={category.name}>
                  <tr
                    data-category-row={category.name}
                    className={rowClass(category.name)}
                  >
                    <td className={td}>{renderIconCell(category, 32)}</td>
                    <td className={td}>
                      <strong>{category.name}</strong>
                    </td>
                    <td className={td}>Главный экран</td>
                    <td className={td}>{category.department ?? '—'}</td>
                    <td className={td}>
                      <input
                        type="checkbox"
                        checked={category.isActive}
                        onChange={(e) =>
                          void toggleActive(category, e.target.checked)
                        }
                        disabled={busy}
                        className="h-4 w-4 cursor-pointer accent-ok"
                        title="Отключённая категория скрыта с кассы"
                      />
                    </td>
                    <td className={tdNum}>{counts.get(category.name) ?? 0}</td>
                    <td className={td}>{renderActions(category)}</td>
                  </tr>
                  {childrenOf(category.name)
                    .filter(matches)
                    .map((child) => (
                      <tr
                        key={`${category.name}/${child.name}`}
                        data-category-row={child.name}
                        className={rowClass(child.name)}
                      >
                        <td className={td}>{renderIconCell(child, 28)}</td>
                        <td className={td}>
                          <span className="pl-4">↳ {child.name}</span>
                        </td>
                        <td className={td}>{category.name}</td>
                        <td className={td}>{child.department ?? '—'}</td>
                        <td className={td}>
                          <input
                            type="checkbox"
                            checked={child.isActive}
                            onChange={(e) =>
                              void toggleActive(child, e.target.checked)
                            }
                            disabled={busy}
                            className="h-4 w-4 cursor-pointer accent-ok"
                            title="Отключённая категория скрыта с кассы"
                          />
                        </td>
                        <td className={tdNum}>
                          {counts.get(`${category.name}\u0000${child.name}`) ?? 0}
                        </td>
                        <td className={td}>{renderActions(child)}</td>
                      </tr>
                    ))}
                </Fragment>
              ))}
              {orphans.filter(matches).map((category) => (
                <tr
                  key={`orphan/${category.name}`}
                  data-category-row={category.name}
                  className={rowClass(category.name)}
                >
                  <td className={td}>{renderIconCell(category, 28)}</td>
                  <td className={td}>{category.name}</td>
                  <td className={td}>{category.parentName} (не найден)</td>
                  <td className={td}>{category.department ?? '—'}</td>
                  <td className={td}>
                    <input
                      type="checkbox"
                      checked={category.isActive}
                      onChange={(e) =>
                        void toggleActive(category, e.target.checked)
                      }
                      disabled={busy}
                      className="h-4 w-4 cursor-pointer accent-ok"
                      title="Отключённая категория скрыта с кассы"
                    />
                  </td>
                  <td className={tdNum}>{counts.get(category.name) ?? 0}</td>
                  <td className={td}>{renderActions(category)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formOpen && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label={creating ? 'Новая категория' : 'Редактирование категории'}
        >
          <form className={modalSm} onSubmit={submitForm}>
            <h3 className="mt-0 text-big font-bold">
              {creating ? 'Новая категория' : `Категория «${editingName}»`}
            </h3>

            <label className={fieldLabel}>
              Название
              <input
                className={input}
                value={draft.name}
                onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="Например, Сосиски"
                autoFocus
              />
            </label>

            <label className={fieldLabel}>
              Родитель
              <select
                className={input}
                value={draft.parentName}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, parentName: e.target.value }))
                }
              >
                <option value="">Главный экран</option>
                {parentOptions.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.parentName ? `↳ ${c.name}` : c.name}
                  </option>
                ))}
              </select>
            </label>

            <label className={fieldLabel}>
              Отдел
              <input
                className={input}
                value={draft.department}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, department: e.target.value }))
                }
                placeholder="Магазин"
              />
            </label>

            <div className={fieldLabel}>
              Иконка
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <CategoryImage
                  name={draft.name || 'Категория'}
                  hasImage={editingHasImage}
                  size={56}
                />
                <button
                  type="button"
                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                  onClick={openIconPicker}
                  disabled={busy}
                >
                  Выбрать изображение
                </button>
                <button
                  type="button"
                  className={cx(btnBase, btn.secondary, btnSize.sm)}
                  onClick={() => {
                    const name = draft.name.trim()
                    if (!name) {
                      setError('Сначала укажите название категории')
                      return
                    }
                    pickImage(name)
                  }}
                  disabled={busy}
                >
                  Загрузить
                </button>
              </div>
              <span className="mt-1 text-tiny font-normal text-muted">
                Готовая иконка с выбором цвета или своё изображение с
                компьютера/телефона.
              </span>
            </div>

            <label className="mb-4 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, isActive: e.target.checked }))
                }
                className="h-4 w-4 accent-ok"
              />
              Активно (показывать на кассе)
            </label>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={cx(btnBase, btn.secondary, btnSize.md)}
                onClick={closeForm}
                disabled={busy}
              >
                Отмена
              </button>
              <button
                type="submit"
                className={cx(btnBase, btn.success, btnSize.md)}
                disabled={busy}
              >
                Сохранить
              </button>
            </div>
          </form>
        </div>
      )}

      {dragGhost && (
        <div
          className="pointer-events-none fixed z-[1200] max-w-[320px] truncate rounded-card border border-accent bg-white px-3 py-1.5 text-small font-semibold text-ink shadow-modal"
          style={{ left: dragGhost.x + 12, top: dragGhost.y + 12 }}
        >
          {dragGhost.name}
        </div>
      )}

      {dragGhost && (
        <div
          className="pointer-events-none fixed z-[1200] max-w-[320px] truncate rounded-card border border-accent bg-white px-3 py-1.5 text-small font-semibold text-ink shadow-modal"
          style={{ left: dragGhost.x + 12, top: dragGhost.y + 12 }}
        >
          {dragGhost.name}
        </div>
      )}

      {iconPicker && (
        <div
          className={cx(overlay, 'z-modal')}
          role="dialog"
          aria-modal="true"
          aria-label="Выбор иконки"
        >
          <div className={modalSm}>
            <h3 className="mt-0 text-big font-bold">Иконка категории</h3>
            <p className={hint}>
              Выберите иконку и утверждённый цвет или загрузите своё
              изображение.
            </p>

            <div className="mb-3 grid grid-cols-6 gap-2">
              {CATEGORY_ICON_KINDS.map((kind) => (
                <button
                  key={kind}
                  type="button"
                  aria-label={`Иконка ${kind}`}
                  onClick={() =>
                    setIconPicker((current) =>
                      current ? { ...current, kind } : current
                    )
                  }
                  className={cx(
                    'cursor-pointer rounded-card border p-1 transition',
                    iconPicker.kind === kind
                      ? 'border-accent ring-2 ring-accent/30'
                      : 'border-line hover:border-lineStrong'
                  )}
                >
                  <CategoryIconTile
                    kind={kind}
                    colorKey={iconPicker.colorKey}
                    size={44}
                  />
                </button>
              ))}
            </div>

            <div className="mb-4 flex flex-wrap gap-2">
              {CATEGORY_PALETTE.map((gradient, index) => (
                <button
                  key={`${gradient.from}-${gradient.to}`}
                  type="button"
                  aria-label={`Цвет ${index + 1}`}
                  onClick={() =>
                    setIconPicker((current) =>
                      current ? { ...current, colorKey: index } : current
                    )
                  }
                  className={cx(
                    'h-8 w-8 cursor-pointer rounded-full border-2',
                    iconPicker.colorKey === index
                      ? 'border-ink'
                      : 'border-transparent'
                  )}
                  style={{
                    background: `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`
                  }}
                />
              ))}
            </div>

            <div className="mb-4 flex items-center gap-3">
              <CategoryIconTile
                kind={iconPicker.kind}
                colorKey={iconPicker.colorKey}
                size={96}
                svgRef={previewRef}
              />
              <span className="text-small text-muted">
                Предпросмотр 256×256. На кассе иконка сохранится как PNG.
              </span>
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                className={cx(btnBase, btn.secondary, btnSize.md)}
                onClick={() => setIconPicker(null)}
                disabled={busy}
              >
                Отмена
              </button>
              <button
                type="button"
                className={cx(btnBase, btn.success, btnSize.md)}
                onClick={() => void applyGeneratedIcon()}
                disabled={busy}
              >
                Применить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/** Склейка классов (локальная, чтобы не тянуть лишний импорт в файл). */
function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(' ')
}
